import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Calendar, Clock, Zap, Banknote } from "lucide-react";
import { RAFFLE } from "@/lib/raffleData";

const pad = (n) => String(n).padStart(2, "0");

function useCountdown(targetISO) {
  const target = new Date(targetISO).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, done: diff === 0 };
}

const Unit = ({ value, label }) => (
  <div className="flex-1 min-w-[70px]">
    <div className="relative rounded-2xl border-2 border-dashed border-[#051A0F]/25 bg-gradient-to-b from-[#FFF5CC] to-[#F0B91B] px-4 py-4 md:py-6 text-center overflow-hidden shadow-[inset_0_-6px_20px_rgba(0,0,0,0.15)]">
      <div className="font-bebas text-4xl md:text-6xl text-[#051A0F] leading-none tracking-wider glow-pulse">
        {pad(value)}
      </div>
      <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#051A0F]/70 font-outfit font-bold">
        {label}
      </div>
    </div>
  </div>
);

export default function TicketAndCountdown() {
  const { days, hours, minutes, seconds } = useCountdown(RAFFLE.drawDateISO);

  return (
    <section
      id="ficha"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="ticket-section"
    >
      <div className="absolute inset-0 bg-[#051A0F]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full radial-gold opacity-70" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F0B91B]/30 bg-[#F0B91B]/5 mb-6">
            <Zap className="w-4 h-4 text-[#F0B91B]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#F0B91B] font-semibold font-outfit">
              Tu ficha dorada
            </span>
          </div>
          <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider">
            Solo <span className="gold-shimmer">$50.000</span> para ganarlo todo
          </h2>
        </motion.div>

        {/* Ficha */}
        <motion.div
          id="sorteo"
          initial={{ opacity: 0, scale: 0.9, rotate: -1 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
          data-testid="ticket-card"
        >
          <div className="absolute -inset-2 bg-gradient-to-r from-[#F0B91B] via-[#FFD700] to-[#F0B91B] blur-2xl opacity-40 rounded-3xl" />

          <div className="relative bg-gradient-to-br from-[#FFD700] via-[#F0B91B] to-[#E5A200] rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-30px_rgba(240,185,27,0.8)]">
            <div className="absolute top-1/2 -left-4 -translate-y-1/2 w-8 h-8 rounded-full bg-[#051A0F]" />
            <div className="absolute top-1/2 -right-4 -translate-y-1/2 w-8 h-8 rounded-full bg-[#051A0F]" />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
              {/* Left - price */}
              <div className="text-center lg:text-left">
                <div className="text-xs uppercase tracking-[0.3em] text-[#051A0F]/70 font-outfit font-bold">
                  Valor de la ficha
                </div>
                <div className="mt-2 font-bebas text-7xl md:text-8xl text-[#051A0F] leading-none tracking-wider">
                  ${RAFFLE.fichaPrice}
                </div>
                <div className="mt-1 text-lg text-[#051A0F]/80 font-outfit font-medium">
                  Pesos colombianos
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-3 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 bg-[#051A0F] text-[#F0B91B] px-4 py-2 rounded-full">
                    <Banknote className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-widest font-outfit font-semibold">
                      Anticipada {RAFFLE.earlyDateShort}
                    </span>
                  </div>
                </div>
              </div>

              {/* Perforated separator */}
              <div className="hidden lg:flex flex-col items-center gap-2 self-stretch">
                {Array.from({ length: 16 }).map((_, i) => (
                  <span key={i} className="w-1.5 h-4 rounded-full bg-[#051A0F]/25" />
                ))}
              </div>
              <div className="lg:hidden">
                <div className="border-t-2 border-dashed border-[#051A0F]/30" />
              </div>

              {/* Right - draw date */}
              <div className="text-center lg:text-left">
                <div className="flex items-center gap-2 mb-3 justify-center lg:justify-start">
                  <Clock className="w-4 h-4 text-[#051A0F]" />
                  <span className="text-xs uppercase tracking-[0.3em] text-[#051A0F]/80 font-outfit font-bold">
                    Sorteo Oficial
                  </span>
                </div>
                <div className="font-bebas text-4xl md:text-5xl text-[#051A0F] leading-none tracking-wider">
                  {RAFFLE.drawDate}
                </div>
                <p className="mt-2 text-sm text-[#051A0F]/70 font-outfit">
                  {RAFFLE.drawDateNote} · Transmisión en vivo
                </p>

                <a
                  href="#elegir-ficha"
                  className="mt-6 inline-flex items-center gap-2 bg-[#051A0F] hover:bg-[#0A2818] text-[#F0B91B] px-6 py-3 rounded-full font-bebas text-xl tracking-wider transition-all hover:-translate-y-0.5 shadow-lg"
                  data-testid="ticket-cta"
                >
                  Adquirir mi Ficha
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two key dates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto"
        >
          <div className="rounded-2xl border border-[#E63946]/30 bg-[#0A2818]/60 backdrop-blur-md p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#E63946]/15 flex items-center justify-center flex-shrink-0">
              <Banknote className="w-5 h-5 text-[#E63946]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/60 font-outfit">
                Entrega anticipada · ${RAFFLE.earlyPrize}
              </div>
              <div className="font-bebas text-2xl text-[#FDFBF7] tracking-wide">
                {RAFFLE.earlyDate}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0B91B]/80 font-outfit mt-0.5">
                {RAFFLE.earlyDateNote}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-[#F0B91B]/30 bg-[#0A2818]/60 backdrop-blur-md p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#F0B91B]/15 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-[#F0B91B]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/60 font-outfit">
                Sorteo del Gran Proyecto
              </div>
              <div className="font-bebas text-2xl text-[#FDFBF7] tracking-wide">
                {RAFFLE.drawDate}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0B91B]/80 font-outfit mt-0.5">
                {RAFFLE.drawDateNote}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Countdown */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-14 text-center"
          data-testid="countdown"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#F0B91B] font-outfit font-semibold mb-6">
            Faltan para el sorteo
          </div>
          <div className="flex gap-3 md:gap-4 justify-center max-w-3xl mx-auto">
            <Unit value={days} label="Días" />
            <Unit value={hours} label="Horas" />
            <Unit value={minutes} label="Minutos" />
            <Unit value={seconds} label="Segundos" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
